import { useEffect, useMemo, useState } from "react";
import type { ComponentType, FormEvent, ReactNode } from "react";
import {
  Box,
  CheckCircle2,
  Code2,
  Database,
  GitBranch,
  KeyRound,
  PlugZap,
  Plus,
  RefreshCw,
  Search,
  Server,
  ShieldCheck,
  Trash2,
  Wrench,
} from "lucide-react";
import { Input } from "@/components/atoms/studio/ui/input";
import { Skeleton } from "@/components/atoms/studio/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  useMcpConnectorPresets,
  useMcpTools,
  useMcpConnectors,
  useCreateMcpConnector,
  useStartMcpOAuth,
  useDeleteMcpConnector,
  useDiscoverMcpConnector,
} from "@/hooks/tools";
import type {
  McpConnector,
  McpConnectorPreset,
  McpGatewayTool,
  StartMcpOAuthInput,
} from "@/services/mcp-gateway";

const FALLBACK_CONNECTOR_PRESETS: McpConnectorPreset[] = [
  {
    key: "jira",
    title: "Jira",
    description: "Jira MCP for issues, projects, releases, and sprint context.",
    base_url_placeholder: "https://mcp.atlassian.com/v1/mcp",
    credential_label: "Jira OAuth token",
    auth_type: "oauth",
  },
  {
    key: "notion",
    title: "Notion",
    description: "Notion MCP for pages, databases, specs, and run artifacts.",
    base_url_placeholder: "https://mcp.notion.com/mcp",
    credential_label: "Notion OAuth token",
    auth_type: "oauth",
  },
];

type ToolFamily = {
  key: string;
  title: string;
  icon: ComponentType<{ className?: string }>;
  tone: string;
};

type ToolInventoryItem = {
  id: string;
  name: string;
  server: string;
  description: string;
  family: ToolFamily;
  live: boolean;
  schema: Record<string, unknown>;
  canQuickTest: boolean;
};

function humanMcpError(error?: string | null): string | null {
  if (!error) return null;
  if (error.includes("jira_oauth_expired_reconnect_required"))
    return "Jira login expired. Reconnect Jira to refresh the MCP token.";
  if (error.includes("notion_oauth_expired_reconnect_required"))
    return "Notion login expired. Reconnect Notion to refresh the MCP token.";
  if (error.includes("linear_oauth_expired_reconnect_required"))
    return "Linear login expired. Reconnect Linear to refresh the MCP token.";
  if (error.includes("state_not_found"))
    return "The login session expired or was already used. Start the login again from this page.";
  if (error.includes("missing_code_or_state"))
    return "The login callback was missing required OAuth data. Start the login again.";
  if (error.includes("monday_oauth_not_configured"))
    return "Monday OAuth app credentials are missing in the MCP gateway.";
  if (error.includes("jira_oauth_not_configured"))
    return "Jira OAuth app credentials are missing in the MCP gateway.";
  if (error.includes("linear_oauth_not_configured"))
    return "Linear OAuth is not available from the MCP gateway. Refresh the page and try again.";
  return error;
}

function mutationErrorMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown; message?: unknown } } })
    ?.response?.data;
  const backendMessage = detail?.detail || detail?.message;
  if (typeof backendMessage === "string" && backendMessage.trim())
    return humanMcpError(backendMessage) || backendMessage;
  if (error instanceof Error && error.message.trim())
    return humanMcpError(error.message) || error.message;
  return fallback;
}

function oauthNoticeFromUrl(): { tone: "success" | "error"; message: string } | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const connected = params.get("mcp_connected");
  if (connected) return { tone: "success", message: `${connected} MCP connected.` };
  const error = params.get("mcp_oauth_detail") || params.get("mcp_oauth_error");
  const message = humanMcpError(error);
  return message ? { tone: "error", message } : null;
}

function clearMcpOAuthUrlParams() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const keys = ["mcp_connected", "connector_id", "mcp_oauth_error", "mcp_oauth_detail"];
  let changed = false;
  for (const key of keys) {
    if (url.searchParams.has(key)) { url.searchParams.delete(key); changed = true; }
  }
  if (changed) window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

function familyFor(tool: string, server?: string): ToolFamily {
  const value = `${server ?? ""} ${tool}`.toLowerCase();
  if (value.includes("graph"))
    return { key: "graph", title: "Graph", icon: GitBranch, tone: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" };
  if (value.includes("metadata") || value.startsWith("list_") || value.includes(" read_"))
    return { key: "metadata", title: "Metadata", icon: Database, tone: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200" };
  if (value.includes("salesforce") || value.includes("tooling") || value.includes("apex") || value.includes("soql"))
    return { key: "salesforce", title: "Salesforce", icon: Code2, tone: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200" };
  if (value.includes("rag") || value.includes("semantic") || value.includes("similar") || value.includes("vector"))
    return { key: "rag", title: "RAG", icon: Box, tone: "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200" };
  if (value.includes("security") || value.includes("permission") || value.includes("access"))
    return { key: "security", title: "Security", icon: ShieldCheck, tone: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" };
  return { key: "other", title: "MCP", icon: Wrench, tone: "bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200" };
}

function buildInventory(liveTools: McpGatewayTool[]): ToolInventoryItem[] {
  return liveTools
    .flatMap((tool) => {
      if (!tool.name) return [];
      const server = tool.connector_name || tool.provider || "mcp";
      const required = Array.isArray(tool.schema?.required) ? tool.schema.required : [];
      return [{ id: tool.id, name: tool.name, server, description: tool.description || "No schema description returned by the MCP endpoint.", family: familyFor(tool.name, server), live: true, schema: tool.schema || {}, canQuickTest: required.length === 0 }];
    })
    .sort((a, b) => {
      if (a.live !== b.live) return a.live ? -1 : 1;
      if (a.family.title !== b.family.title) return a.family.title.localeCompare(b.family.title);
      return a.name.localeCompare(b.name);
    });
}

function cleanToolName(name: string): string { return name.replace(/[_-]+/g, " "); }

function shortToolDescription(description: string): string {
  const text = description.replace(/<example[\s\S]*?<\/example>/gi, " ").replace(/```[\s\S]*?```/g, " ").replace(/[#*_`>{}\[\]\t]/g, " ").replace(/\s+/g, " ").trim();
  if (!text) return "No description returned by the MCP endpoint.";
  const sentence = text.match(/^(.{30,220}?[.!?])\s/)?.[1] ?? text.slice(0, 180);
  return sentence.length < text.length ? `${sentence.replace(/[.,;:\s]+$/, "")}.` : sentence;
}

function schemaInputNames(schema: Record<string, unknown>): { required: string[]; optional: string[] } {
  const required = Array.isArray(schema.required) ? schema.required.map((item) => String(item)).filter(Boolean) : [];
  const properties = schema.properties && typeof schema.properties === "object" && !Array.isArray(schema.properties) ? Object.keys(schema.properties as Record<string, unknown>) : [];
  return { required, optional: properties.filter((item) => !required.includes(item)) };
}

function providerTitle(connector: McpConnector, presets: McpConnectorPreset[]): string {
  return presets.find((preset) => preset.key === connector.provider)?.title ?? connector.provider;
}

export function ToolsLayout() {
  const mcpToolsQ = useMcpTools();
  const presetsQ = useMcpConnectorPresets();
  const connectorsQ = useMcpConnectors();
  const createConnectorM = useCreateMcpConnector();
  const startOAuthM = useStartMcpOAuth();
  const deleteConnectorM = useDeleteMcpConnector();
  const discoverConnectorM = useDiscoverMcpConnector();

  const connectorPresets = presetsQ.data?.length ? presetsQ.data : FALLBACK_CONNECTOR_PRESETS;
  const [query, setQuery] = useState("");
  const [selectedPresetKey, setSelectedPresetKey] = useState(connectorPresets[0].key);
  const selectedPreset = connectorPresets.find((p) => p.key === selectedPresetKey) ?? connectorPresets[0];
  const selectedAuthType = selectedPreset.auth_type || "oauth";
  const [connectorName, setConnectorName] = useState(selectedPreset.title);
  const [connectorUrl, setConnectorUrl] = useState("");
  const [credentialLabel, setCredentialLabel] = useState(selectedPreset.credential_label);
  const [credentialSecret, setCredentialSecret] = useState("");
  const [oauthNotice, setOauthNotice] = useState<{ tone: "success" | "error"; message: string } | null>(() => oauthNoticeFromUrl());

  const liveTools = mcpToolsQ.data ?? [];
  const connectors = connectorsQ.data ?? [];

  useEffect(() => {
    const nextPreset = connectorPresets.find((p) => p.key === selectedPresetKey) ?? connectorPresets[0];
    if (!nextPreset) return;
    setConnectorName((c) => c || nextPreset.title);
    setCredentialLabel((c) => c || nextPreset.credential_label);
  }, [connectorPresets, selectedPresetKey]);

  useEffect(() => {
    const notice = oauthNoticeFromUrl();
    if (!notice) return;
    setOauthNotice(notice);
    clearMcpOAuthUrlParams();
    void connectorsQ.refetch();
    void mcpToolsQ.refetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inventory = useMemo(() => buildInventory(liveTools), [liveTools]);
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return inventory;
    return inventory.filter((tool) =>
      tool.name.toLowerCase().includes(q) ||
      tool.server.toLowerCase().includes(q) ||
      tool.description.toLowerCase().includes(q)
    );
  }, [inventory, q]);

  const familyCounts = useMemo(() => {
    const counts = new Map<string, { family: ToolFamily; count: number }>();
    for (const tool of inventory) {
      const current = counts.get(tool.family.key) ?? { family: tool.family, count: 0 };
      current.count += 1;
      counts.set(tool.family.key, current);
    }
    return Array.from(counts.values()).sort((a, b) => b.count - a.count);
  }, [inventory]);

  const selectPreset = (key: string) => {
    const preset = connectorPresets.find((p) => p.key === key) ?? connectorPresets[0];
    setSelectedPresetKey(preset.key);
    setConnectorName(preset.title);
    setConnectorUrl("");
    setCredentialLabel(preset.credential_label);
    setCredentialSecret("");
  };

  const saveConnector = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = connectorName.trim();
    const baseUrl = connectorUrl.trim();
    if (selectedAuthType === "oauth") {
      if (startOAuthM.isPending) return;
      startOAuthM.mutate({ provider: selectedPreset.key as StartMcpOAuthInput["provider"], name: connectorName.trim() || selectedPreset.title });
      return;
    }
    if (!name || !baseUrl || createConnectorM.isPending) return;
    createConnectorM.mutate(
      { provider: selectedPreset.key, name, base_url: baseUrl, credential_label: credentialLabel.trim() || selectedPreset.credential_label, credential_secret: credentialSecret },
      { onSuccess: () => { setCredentialSecret(""); setConnectorUrl(""); } },
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Metric label="Live MCP tools" value={String(liveTools.length)} />
        <Metric label="Tool families" value={String(familyCounts.length)} />
        <Metric label="Connectors" value={String(connectors.length)} />
      </div>

      <div className="studio-card p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Connect a new MCP</h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Connect Jira, Monday.com, Linear, or Notion here. Chat can only use providers after they are connected.
            </p>
          </div>
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            OAuth providers redirect to login and the MCP gateway stores tokens server-side.
          </div>
        </div>

        {oauthNotice ? (
          <div className={cn("mt-4 rounded-md border p-3 text-sm", oauthNotice.tone === "success" ? "border-success/30 bg-success/5 text-success" : "border-destructive/30 bg-destructive/5 text-destructive")}>
            {oauthNotice.message}
          </div>
        ) : null}

        <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,0.9fr)_minmax(360px,0.7fr)]">
          <div className="grid gap-3 md:grid-cols-2">
            {connectorPresets.map((preset) => (
              <button key={preset.key} type="button" onClick={() => selectPreset(preset.key)}
                className={cn("rounded-lg border p-4 text-left transition-colors", selectedPreset.key === preset.key ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-muted/60")}>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-2 font-semibold">
                    <PlugZap className="h-4 w-4 text-primary" />{preset.title}
                  </span>
                  {selectedPreset.key === preset.key ? <CheckCircle2 className="h-4 w-4 text-primary" /> : null}
                </div>
                <p className="text-sm leading-6 text-muted-foreground">{preset.description}</p>
              </button>
            ))}
          </div>

          <form onSubmit={saveConnector} className="rounded-lg border border-border bg-card p-4">
            <div className="mb-4 flex items-center gap-2">
              <Server className="h-4 w-4 text-primary" />
              <h3 className="font-semibold">{selectedPreset.title} connection</h3>
            </div>
            <div className="space-y-3">
              <Field label="Connection name">
                <Input value={connectorName} onChange={(e) => setConnectorName(e.target.value)} placeholder={`${selectedPreset.title} production`} />
              </Field>
              {selectedAuthType === "oauth" ? (
                <div className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                  Clicking connect will open {selectedPreset.title} login and consent. Tokens are stored server-side by the MCP gateway.
                </div>
              ) : (
                <>
                  <Field label="Base URL">
                    <Input value={connectorUrl} onChange={(e) => setConnectorUrl(e.target.value)} placeholder={selectedPreset.base_url_placeholder} />
                  </Field>
                  <Field label="Credential name">
                    <Input value={credentialLabel} onChange={(e) => setCredentialLabel(e.target.value)} placeholder={selectedPreset.credential_label} />
                  </Field>
                  <Field label="Credential">
                    <div className="relative">
                      <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input type="password" value={credentialSecret} onChange={(e) => setCredentialSecret(e.target.value)} placeholder="Paste token, API key, or gateway secret" className="pl-9" />
                    </div>
                  </Field>
                </>
              )}
              {startOAuthM.isError ? (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                  {mutationErrorMessage(startOAuthM.error, `Could not start ${selectedPreset.title} OAuth login from the MCP gateway.`)}
                </div>
              ) : null}
              {createConnectorM.isError ? (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                  Could not save connector in MCP gateway.
                </div>
              ) : null}
              <button type="submit" className="studio-button studio-button-primary w-full"
                disabled={!connectorName.trim() || (selectedAuthType === "manual" && !connectorUrl.trim()) || createConnectorM.isPending || startOAuthM.isPending}>
                {selectedAuthType === "oauth" ? <PlugZap className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {selectedAuthType === "oauth"
                  ? startOAuthM.isPending ? "Opening login..." : `Continue with ${selectedPreset.title}`
                  : createConnectorM.isPending ? "Adding..." : `Add ${selectedPreset.title} connector`}
              </button>
            </div>
          </form>
        </div>

        {connectorsQ.isError ? (
          <div className="mt-5 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            Could not load MCP connectors from the gateway.
          </div>
        ) : connectorsQ.isLoading ? (
          <div className="mt-5 grid gap-3">
            {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}
          </div>
        ) : connectors.length ? (
          <div className="mt-5 divide-y divide-border rounded-md border border-border">
            {connectors.map((connector) => (
              <div key={connector.id} className="grid gap-3 p-3 md:grid-cols-[minmax(0,1fr)_180px_auto] md:items-center">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{connector.name}</span>
                    <span className="rounded bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">{providerTitle(connector, connectorPresets)}</span>
                    <span className={cn("rounded px-2 py-0.5 text-[11px] font-semibold", connector.status === "live" ? "bg-success/10 text-success" : connector.status === "error" ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground")}>
                      {connector.status}
                    </span>
                  </div>
                  <div className="mt-1 truncate font-mono text-xs text-muted-foreground">{connector.base_url}</div>
                  {connector.last_error ? <div className="mt-1 line-clamp-2 text-xs text-destructive">{humanMcpError(connector.last_error)}</div> : null}
                </div>
                <div className="text-xs text-muted-foreground">
                  <div>{connector.auth_type === "oauth" ? "OAuth" : connector.credential_label}: {connector.secret_preview}</div>
                  <div>{connector.tool_count} discovered tool{connector.tool_count === 1 ? "" : "s"}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {connector.auth_type === "oauth" && connector.status === "error" ? (
                    <button type="button" onClick={() => startOAuthM.mutate({ provider: connector.provider as StartMcpOAuthInput["provider"], name: connector.name })}
                      className="studio-button studio-button-primary" disabled={startOAuthM.isPending}>
                      <PlugZap className="h-4 w-4" /> Reconnect
                    </button>
                  ) : null}
                  <button type="button" onClick={() => discoverConnectorM.mutate(connector.id)} className="studio-button"
                    disabled={discoverConnectorM.isPending && discoverConnectorM.variables === connector.id}>
                    <RefreshCw className={cn("h-4 w-4", discoverConnectorM.isPending && discoverConnectorM.variables === connector.id && "animate-spin")} /> Discover
                  </button>
                  <button type="button" onClick={() => deleteConnectorM.mutate(connector.id)} className="studio-button" disabled={deleteConnectorM.isPending}>
                    <Trash2 className="h-4 w-4" /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="studio-card p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Available MCP tools</h2>
            <p className="mt-1 text-sm text-muted-foreground">{filtered.length} shown from {inventory.length} total.</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button type="button" className="studio-button" onClick={() => void mcpToolsQ.refetch()} disabled={mcpToolsQ.isFetching}>
              <RefreshCw className={cn("h-4 w-4", mcpToolsQ.isFetching && "animate-spin")} /> Refresh tools
            </button>
            <label className="flex h-10 min-w-[280px] items-center gap-2 rounded-md border border-border bg-card px-3">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search MCP tools..." className="h-auto border-0 bg-transparent px-0 shadow-none focus-visible:ring-0" />
            </label>
          </div>
        </div>

        {mcpToolsQ.isFetching ? (
          <div className="mt-5 grid gap-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)}</div>
        ) : !mcpToolsQ.isFetched ? (
          <div className="mt-5 rounded-md border bg-muted/30 p-4 text-sm text-muted-foreground">Connect a provider and click Discover to populate its tools.</div>
        ) : mcpToolsQ.isError ? (
          <div className="mt-5 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">Could not load live MCP tools.</div>
        ) : filtered.length === 0 ? (
          <div className="mt-5 rounded-md border bg-muted/30 p-4 text-sm text-muted-foreground">No MCP tools match the current search.</div>
        ) : (
          <div className="mt-5 overflow-hidden rounded-md border border-border">
            <div className="hidden grid-cols-[140px_minmax(180px,1.1fr)_minmax(240px,1.5fr)_minmax(180px,1fr)] gap-3 border-b bg-muted/40 px-4 py-3 text-xs font-semibold uppercase text-muted-foreground lg:grid">
              <div>Provider</div><div>Tool</div><div>Purpose</div><div>Inputs</div>
            </div>
            {filtered.map((tool) => (
              <ToolRow key={tool.id} tool={tool} />
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {familyCounts.map(({ family, count }) => (
          <div key={family.key} className="studio-card p-4">
            <div className={cn("mb-3 inline-flex h-9 w-9 items-center justify-center rounded-md", family.tone)}>
              <family.icon className="h-4 w-4" />
            </div>
            <div className="text-lg font-semibold">{family.title}</div>
            <p className="mt-1 text-sm text-muted-foreground">{count} tool{count === 1 ? "" : "s"}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="studio-card p-4">
      <div className="text-xs font-semibold uppercase text-muted-foreground">{label}</div>
      <div className="mt-2 text-3xl font-bold">{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-semibold uppercase text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function ToolRow({ tool }: { tool: ToolInventoryItem }) {
  const { required, optional } = schemaInputNames(tool.schema);
  const visibleRequired = required.slice(0, 3);
  const visibleOptional = optional.slice(0, Math.max(0, 4 - visibleRequired.length));
  const hiddenInputCount = Math.max(0, required.length + optional.length - visibleRequired.length - visibleOptional.length);
  const summary = shortToolDescription(tool.description);

  return (
    <div className="grid gap-3 border-b border-border p-4 last:border-b-0 lg:grid-cols-[140px_minmax(180px,1.1fr)_minmax(240px,1.5fr)_minmax(180px,1fr)] lg:items-start">
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase text-muted-foreground lg:hidden">Provider</div>
        <div className="mt-1 flex min-w-0 items-center gap-2 lg:mt-0">
          <span className={cn("inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md", tool.family.tone)}>
            <tool.family.icon className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{tool.server}</div>
            <div className="text-xs text-muted-foreground">{tool.family.title}</div>
          </div>
        </div>
      </div>
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase text-muted-foreground lg:hidden">Tool</div>
        <div className="mt-1 break-words font-mono text-sm font-semibold lg:mt-0" title={tool.name}>{cleanToolName(tool.name)}</div>
        <div className={cn("mt-2 inline-flex rounded px-2 py-0.5 text-[11px] font-semibold", tool.live ? "bg-success/10 text-success" : "bg-warning/10 text-warning")}>
          {tool.live ? "live" : "referenced"}
        </div>
      </div>
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase text-muted-foreground lg:hidden">Purpose</div>
        <p className="mt-1 text-sm leading-6 text-muted-foreground lg:mt-0">{summary}</p>
        {tool.description && tool.description.length > summary.length + 40 ? (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs font-semibold text-primary">View full MCP description</summary>
            <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">{tool.description}</pre>
          </details>
        ) : null}
      </div>
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase text-muted-foreground lg:hidden">Inputs</div>
        <div className="mt-1 flex flex-wrap gap-1.5 lg:mt-0">
          {visibleRequired.map((name) => <span key={`r-${name}`} className="rounded border border-primary/30 bg-primary/5 px-2 py-1 font-mono text-[11px] text-primary">{name}</span>)}
          {visibleOptional.map((name) => <span key={`o-${name}`} className="rounded border border-border bg-muted/30 px-2 py-1 font-mono text-[11px] text-muted-foreground">{name}</span>)}
          {hiddenInputCount ? <span className="rounded border border-border bg-muted/30 px-2 py-1 text-[11px] text-muted-foreground">+{hiddenInputCount}</span> : null}
          {!required.length && !optional.length ? <span className="text-sm text-muted-foreground">No inputs</span> : null}
        </div>
      </div>
    </div>
  );
}
